import inBetween from '../../../../helpers/inBetween';
import { getFocusPoint, getMovementShift, Point } from './zoomerUtils';

interface NavigatorConfig {
  wrapper: HTMLDivElement;
  navigation: HTMLUListElement;
  vertical: boolean | undefined;
  grabbingClass?: string;
  grabbableClass?: string;
  prev: HTMLButtonElement;
  next: HTMLButtonElement;
}

interface NavigatorInstance {
  mouseDown: (e: Event) => void;
  mouseMove: (e: Event) => void;
  mouseUp: (e: Event) => void;
  mouseLeave: (e: Event) => void;
  resize: () => void;
}

function Navigator({
  wrapper,
  navigation,
  vertical,
  grabbingClass = 'viewer-nav--grabbing',
  grabbableClass = 'viewer-nav--grabbable',
  prev,
  next
}: NavigatorConfig): NavigatorInstance {
  let wWidth: number;
  let wHeight: number;
  let nWidth: number;
  let nHeight: number;

  let isGrabX: boolean;
  let isGrabY: boolean;

  let pointerDown: boolean;
  let currentTranslate = { x: 0, y: 0 };

  let currentFocusPoint;
  let previousFocusPoint: Point;

  let prevDisabled: boolean;
  let nextDisabled: boolean;

  (function setInitial(): void {
    setValues();
    setControls();
    disableButtons();
    currentTranslate = { x: 0, y: 0 };
  })();

  function mouseDown(event: Event): void {
    pointerDown = true;
    previousFocusPoint = getFocusPoint(event);
  }

  function mouseMove(event: Event): void {
    event.preventDefault();
    if (pointerDown && (vertical ? isGrabY : isGrabX)) {
      const { x, y } = currentTranslate;
      currentFocusPoint = getFocusPoint(event);
      const { x: movX, y: movY } = getMovementShift(currentFocusPoint, previousFocusPoint);
      currentTranslate = {
        x: isGrabX ? inBetween(wWidth - nWidth, 0, x + movX) : 0,
        y: isGrabY ? inBetween(wHeight - nHeight, 0, y + movY) : 0
      };
      previousFocusPoint = currentFocusPoint;
      disableButtons();
      transformNavigation();
      navigation.classList.add(grabbingClass);
    }
  }

  function mouseUp(): void {
    navigation.classList.remove(grabbingClass);
    pointerDown = false;
  }

  function mouseLeave(): void {
    navigation.classList.remove(grabbingClass);
    pointerDown = false;
  }

  function onNext(e: Event): void {
    if (nextDisabled) {
      e.preventDefault();
      return;
    }

    const { x, y } = currentTranslate;
    const numberOfItems = navigation.children.length;
    const [sizeX, sizeY] = [nWidth / numberOfItems, nHeight / numberOfItems];
    const [shiftX, shiftY] = [(nWidth - wWidth + x) % sizeX, (nHeight - wHeight + y) % sizeY];
    currentTranslate = {
      x: isGrabX ? x - (shiftX === 0 ? sizeX : shiftX) : 0,
      y: isGrabY ? y - (shiftY === 0 ? sizeY : shiftY) : 0
    };
    disableButtons();
    transformNavigation(200);
  }

  function onPrev(e: Event): void {
    if (prevDisabled) {
      e.preventDefault();
      return;
    }

    const { x, y } = currentTranslate;
    const numberOfItems = navigation.children.length;
    const [sizeX, sizeY] = [nWidth / numberOfItems, nHeight / numberOfItems];
    const [shiftX, shiftY] = [x % sizeX, y % sizeY];
    currentTranslate = {
      x: isGrabX ? x + (shiftX === 0 ? sizeX : Math.abs(shiftX)) : 0,
      y: isGrabY ? y + (shiftY === 0 ? sizeY : Math.abs(shiftY)) : 0
    };
    disableButtons();
    transformNavigation(200);
  }

  function resize(): void {
    setValues();
    setControls();
    disableButtons();
    adjustTranslate();
  }

  function adjustTranslate(): void {
    const { x, y } = currentTranslate;
    const widthDiff = wWidth - nWidth;
    const heightDiff = wHeight - nHeight;
    currentTranslate = {
      x: widthDiff > x ? widthDiff : x,
      y: heightDiff > y ? widthDiff : y
    };
    transformNavigation();
  }

  function setControls(): void {
    const controls = {
      add(): void {
        navigation.classList.add(grabbableClass);
        prev.classList.remove('display-none');
        next.classList.remove('display-none');
        prev.addEventListener('click', onPrev);
        next.addEventListener('click', onNext);
      },
      remove(): void {
        navigation.classList.remove(grabbableClass);
        prev.classList.add('display-none');
        next.classList.add('display-none');
        prev.removeEventListener('click', onPrev);
        next.removeEventListener('click', onNext);
      }
    };

    return (vertical && isGrabY) || (!vertical && isGrabX) ? controls.add() : controls.remove();
  }

  function setValues(): {
    wWidth: number;
    wHeight: number;
    nWidth: number;
    nHeight: number;
    isGrabX: boolean;
    isGrabY: boolean;
  } {
    const { width, height } = wrapper.getBoundingClientRect();
    [wWidth, wHeight] = [Math.round(width), Math.round(height)];
    ({ scrollWidth: nWidth, scrollHeight: nHeight } = navigation);
    [isGrabX, isGrabY] = [nWidth > wWidth, nHeight > wHeight];
    return {
      wWidth,
      wHeight,
      nWidth,
      nHeight,
      isGrabX,
      isGrabY
    };
  }

  function disableButtons(): void {
    const { x, y } = currentTranslate;
    prevDisabled = vertical ? y >= 0 : x >= 0;
    nextDisabled = vertical ? y <= wHeight - nHeight : x <= wWidth - nWidth;

    if (prevDisabled) {
      prev.classList.add('disabled');
    } else {
      prev.classList.remove('disabled');
    }

    if (nextDisabled) {
      next.classList.add('disabled');
    } else {
      next.classList.remove('disabled');
    }
  }

  function transformNavigation(speed = 0): void {
    navigation.style.transition = `transform ${speed}ms ease-in-out`;
    navigation.style.transform = vertical
      ? `translateY(${currentTranslate.y}px)`
      : `translateX(${currentTranslate.x}px)`;
  }

  function proxyEvent(handler: (e: Event) => void): (e: Event) => void {
    return (event: Event): void => {
      if (event.type.includes('mouse')) {
        event.preventDefault();
      }
      handler(event);
    };
  }

  return {
    mouseDown: proxyEvent(mouseDown),
    mouseMove: proxyEvent(mouseMove),
    mouseUp: proxyEvent(mouseUp),
    mouseLeave: proxyEvent(mouseLeave),
    resize
  };
}

export default Navigator;
